import { DonationFrequency } from '@web/graphql-components'
import { useEffect, useMemo, useState } from 'react'
import { useDonationFormContext } from '../../DonationFormProvider'
import { DONATION_FREQUENCY_TO_LABEL } from '../../donationUtils'
import { BRAND_KEY, TEXT_DARK_KEY, useColorWithOpacity } from '@web/components/molecules/ColorPicker/utils'
import { useBrandingsStore } from '@web/components/utils/branding/useBrandingsStore'
import { Box, Group } from '@web/components/atoms/Layout'
import PageRadioButton from '../atoms/PageRadioButton'
import { Body2 } from '@web/components/atoms/Typography'
import { Icon } from '@web/components/atoms/Icons'

type GivingFrequencySelectorProps = {
  frequencies: DonationFrequency[]
  defaultFrequency: number | null | undefined
}

const GivingFrequencySelector = ({ frequencies, defaultFrequency }: GivingFrequencySelectorProps) => {
  const [activeButton, setActiveButton] = useState(
    defaultFrequency === null || defaultFrequency === undefined ? 0 : defaultFrequency,
  )
  const givingFrequencies = useMemo(
    () => frequencies.map((frequency) => DONATION_FREQUENCY_TO_LABEL[frequency]),
    [frequencies],
  )
  const styles = useDonationFormContext((state) => state.settings.styles)
  const primaryColorFromBranding = useBrandingsStore(({ colorData }) => colorData['Brand'])
  const selectedHandleColor = useColorWithOpacity(
    styles?.selectedHandleColor,
    styles?.selectedHandleColorOpacity,
    styles?.accentColor ?? primaryColorFromBranding,
  )
  const unselectedHandleColor = useColorWithOpacity(
    styles?.unselectedHandleColor,
    styles?.unselectedHandleColorOpacity,
    '#FFFFFF',
  )
  const selectedTextColor = useColorWithOpacity(
    styles?.selectedHandleTextColor,
    styles?.selectedHandleTextColorOpacity,
    '#FFFFFF',
  )
  const unselectedTextColor = useColorWithOpacity(
    styles?.unselectedHandleTextColor,
    styles?.unselectedHandleTextColorOpacity,
    '#000000',
  )
  const nudgeMessageColor = useColorWithOpacity(
    styles?.nudgeMessageColor ?? BRAND_KEY,
    styles?.nudgeMessageColorOpacity ?? '0.5',
    BRAND_KEY,
  )
  const nudgeMessageTextColor = useColorWithOpacity(styles?.nudgeMessageTextColor, '1', TEXT_DARK_KEY)
  const { setFormValues } = useDonationFormContext((state) => state.actions)
  const formFrequency = useDonationFormContext((state) => state.formValues.givingFrequency)
  const selectedFrequency = formFrequency ? frequencies.indexOf(formFrequency) : null

  useEffect(() => {
    if (defaultFrequency === null || defaultFrequency === undefined) {
      return
    }

    setActiveButton(defaultFrequency)
  }, [defaultFrequency])

  const nudge = styles?.options?.regularGivingNudge
  const nudgeMessage = styles?.options?.regularGivingNudgeMessage
  const selectedOption =
    givingFrequencies[selectedFrequency && selectedFrequency !== -1 ? selectedFrequency : activeButton]
  const isNudgeSelected = selectedFrequency && frequencies[selectedFrequency] === nudge

  return (
    <Box w="100%" position="relative">
      <Group
        attached={true}
        grow={true}
        w="100%"
        css={{
          '@container page (min-width: 0px)': {
            button: {
              fontSize: '16px',
            },
          },
          '@container page (min-width: 500px)': {
            button: {
              fontSize: '20px',
            },
          },
        }}
      >
        {givingFrequencies.map((value, index) => {
          const hasNudge = nudge && frequencies[index] === nudge
          const isSelected = selectedOption === value
          return (
            <PageRadioButton
              key={value}
              isActive={isSelected}
              onClick={() => {
                setActiveButton(index)
                setFormValues({ givingFrequency: frequencies[index] as DonationFrequency })
              }}
              buttonText={value}
              styles={{
                selectedColor: selectedHandleColor,
                unselectedColor: unselectedHandleColor,
                selectedTextColor: selectedTextColor,
                unselectedTextColor: unselectedTextColor,
              }}
              icon={
                hasNudge ? (
                  <Icon
                    transform="scale(0.65)"
                    transition="transform 0.2s ease-in-out"
                    _groupHover={{ transform: 'scale(0.85)' }}
                    _groupActive={{ transform: 'scale(1)', strokeDasharray: '100', strokeDashoffset: '100' }}
                    {...(isSelected
                      ? {
                          css: {
                            '& path': {
                              animation: 'draw',
                            },
                          },
                        }
                      : {})}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" viewBox="0 0 10 9">
                      <path
                        fill="#C53030"
                        stroke="#fff"
                        d="M8.223 1.107c-.856-.73-2.13-.598-2.915.213L5 1.637l-.308-.317C3.908.51 2.633.378 1.777 1.107a2.244 2.244 0 0 0-.155 3.249l3.023 3.122a.49.49 0 0 0 .708 0l3.024-3.122c.88-.908.828-2.411-.154-3.249Z"
                      />
                    </svg>
                  </Icon>
                ) : null
              }
              css={{
                position: 'relative',
                '@container page (max-width: 599px)': {
                  fontSize: '16px',
                  padding: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                },
                ...(hasNudge && nudgeMessage && !isSelected
                  ? {
                      '&::before': {
                        content: '""',
                        display: 'block',
                        width: '0',
                        height: '0',
                        position: 'absolute',
                        bottom: '-22px',
                        borderLeft: '12px solid transparent',
                        borderRight: '12px solid transparent',
                        borderBottom: `12px solid ${nudgeMessageColor}`,
                      },
                    }
                  : {}),
              }}
            />
          )
        })}
      </Group>
      {nudge && nudgeMessage && !isNudgeSelected && (
        <Box
          w="100%"
          px="4px"
          py="8px"
          textAlign="center"
          backgroundColor={nudgeMessageColor}
          borderRadius="4px"
          mt="20px"
        >
          <Body2 color={nudgeMessageTextColor}>{nudgeMessage}</Body2>
        </Box>
      )}
    </Box>
  )
}

export default GivingFrequencySelector
